// =============================================================================
// R-239 P4 Task 10 — LearningBadge. Compact inline badge showing field
// lifecycle state with key metric. Lifecycle color palette mirrors the
// ocr-health panel (Task 9) for consistency:
//   learning → amber #FFC107   proven → green #4CAF50   demoted → red #F44336
// =============================================================================

import type { Lifecycle } from "@capture/shared";
import styles from "./LearningBadge.module.css";

// ---------------------------------------------------------------------------
// Props — a minimal typed subset of ProfileLearningField
// ---------------------------------------------------------------------------

export interface LearningBadgeProps {
  lifecycle: Lifecycle;
  /** Required for proven variant (shown as 0.97). */
  calibratedConfidence: number;
  /** Required for learning variant numerator. */
  agreementCount: number;
  /** Required for learning variant denominator. */
  observationCount: number;
  /** Shown as a % on the proven variant (0.0–1.0 fraction). */
  skipRate: number;
}

// ---------------------------------------------------------------------------
// Lifecycle metadata
// ---------------------------------------------------------------------------

const LIFECYCLE_META: Record<
  Lifecycle,
  { glyph: string; label: string; cls: string }
> = {
  proven: { glyph: "●", label: "Proven", cls: styles.proven ?? "" },
  learning: { glyph: "◐", label: "Learning", cls: styles.learning ?? "" },
  demoted: { glyph: "▲", label: "Demoted", cls: styles.demoted ?? "" },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LearningBadge({
  lifecycle,
  calibratedConfidence,
  agreementCount,
  observationCount,
  skipRate,
}: LearningBadgeProps) {
  const { glyph, label, cls } = LIFECYCLE_META[lifecycle];

  let detail: string;
  let title: string;
  if (lifecycle === "proven") {
    const pct = Math.round(skipRate * 100);
    detail = `${calibratedConfidence.toFixed(2)} · skips ${pct}%`;
    title = `${label}: confidence ${calibratedConfidence.toFixed(2)}, auto-advance rate ${pct}%`;
  } else if (lifecycle === "learning") {
    detail = `${agreementCount}/${observationCount}`;
    title = `${label}: ${agreementCount} agreements out of ${observationCount} observations`;
  } else {
    // demoted
    detail = "review";
    title = `${label}: field needs review — confidence has degraded`;
  }

  return (
    <span
      role="status"
      className={`${styles.badge} ${cls}`}
      title={title}
      aria-label={`${label}: ${detail}`}
    >
      <span className={styles.glyph} aria-hidden="true">
        {glyph}
      </span>{" "}
      {label} · {detail}
    </span>
  );
}
